/**
 * Invoice Form Page
 * Számla létrehozása / szerkesztése
 */

import { api } from '@/api/client';
import { createInvoice } from '@/api/invoices';
import type { Partner } from '@/api/partners';
import { PartnerSearchSelect } from '@/components/partners';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { deriveManualAdvance } from './invoice-advance';
import { INVOICE_STATUSES, INVOICE_TYPES, PAYMENT_METHODS, VAT_RATES } from './mock-data';
import type { InvoiceStatus, InvoiceType, PaymentMethod } from './types';

/** Form-specific invoice item with vatRate as string to support special VAT codes */
interface FormInvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  /** '27' | '18' | '5' | '0' for standard, 'AAM' | 'TAM' | 'EU' | 'EUK' | 'MAA' for special */
  vatRate: string;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
}

/** Get the numeric VAT percentage for a given rate value (special codes = 0%) */
function getVatPercent(vatRate: string): number {
  const numericRates: Record<string, number> = { '27': 27, '18': 18, '5': 5, '0': 0 };
  return numericRates[vatRate] ?? 0;
}

interface FormData {
  type: InvoiceType;
  status: InvoiceStatus;
  partnerId: string;
  partnerName: string;
  partnerAddress: string;
  partnerTaxNumber: string;
  issueDate: string;
  dueDate: string;
  paymentMethod: PaymentMethod;
  items: FormInvoiceItem[];
  notes: string;
}

const DEFAULT_FORM_DATA: FormData = {
  type: 'STANDARD',
  status: 'DRAFT',
  partnerId: '',
  partnerName: '',
  partnerAddress: '',
  partnerTaxNumber: '',
  issueDate: new Date().toISOString().split('T')[0] ?? '',
  dueDate: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? '',
  paymentMethod: 'TRANSFER',
  items: [],
  notes: '',
};

function formatPrice(price: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(price);
}

/** Map form VAT rate strings to API-expected format */
function mapVatRateToApi(vatRate: string): string {
  // Standard rates map to percentage strings; special codes pass through
  const numericRates: Record<string, string> = { '27': '27', '18': '18', '5': '5', '0': '0' };
  return numericRates[vatRate] ?? vatRate;
}

/** FIN-FG-4-6 + FK-4: Advance payment info loaded from quote */
interface AdvanceInfo {
  quoteId: string;
  quoteNumber: string;
  advanceAmount: number;
  advancePercent?: number | undefined;
  advancePaidAt: string;
  partnerName: string;
  quoteTotal?: number | undefined;
}

export function InvoiceFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  // FIN-FG-4-6 + FK-4: Advance deduction from quote or manual input
  const [advanceInfo, setAdvanceInfo] = useState<AdvanceInfo | null>(null);
  const [advanceLoading, setAdvanceLoading] = useState(false);
  // FK-4: Manual advance input (when no quote-linked advance)
  const [manualAdvancePercent, setManualAdvancePercent] = useState<number | null>(null);
  const [manualAdvanceAmount, setManualAdvanceAmount] = useState<number | null>(null);

  // FIN-FG-4-6: Auto-detect advance from quoteId query param
  useEffect(() => {
    const quoteId = searchParams.get('quoteId');
    if (!quoteId || isEditMode) return;

    setAdvanceLoading(true);
    api
      .get<{ data: Record<string, unknown> }>(`/quotations/${quoteId}`)
      .then(response => {
        const q = response.data;
        const advanceAmount = q['advanceAmount'] ? Number(q['advanceAmount']) : 0;
        const advancePaidAt = q['advancePaidAt'] ? String(q['advancePaidAt']) : '';

        if (advanceAmount > 0 && advancePaidAt) {
          setAdvanceInfo({
            quoteId: String(q['id'] ?? quoteId),
            quoteNumber: String(q['quoteNumber'] ?? q['quotationNumber'] ?? ''),
            advanceAmount,
            advancePercent: q['advancePercent'] ? Number(q['advancePercent']) : undefined,
            advancePaidAt,
            partnerName: String(q['partnerName'] ?? ''),
            quoteTotal: q['totalAmount'] ? Number(q['totalAmount']) : undefined,
          });

          // Pre-fill partner data from quote
          setFormData(prev => ({
            ...prev,
            partnerId: String(q['partnerId'] ?? ''),
            partnerName: String(q['partnerName'] ?? ''),
          }));
        }
      })
      .catch(() => {
        // Silently ignore — advance info is optional
      })
      .finally(() => setAdvanceLoading(false));
  }, [searchParams, isEditMode]);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateItem = (index: number, field: keyof FormInvoiceItem, value: string | number) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      if (!item) return prev;

      const updatedItem = { ...item, [field]: value };

      // Recalculate totals if quantity, unit price or VAT rate changes
      if (field === 'quantity' || field === 'unitPrice' || field === 'vatRate') {
        const quantity = field === 'quantity' ? Number(value) : updatedItem.quantity;
        const unitPrice = field === 'unitPrice' ? Number(value) : updatedItem.unitPrice;
        const vatPercent =
          field === 'vatRate' ? getVatPercent(String(value)) : getVatPercent(updatedItem.vatRate);
        updatedItem.netAmount = quantity * unitPrice;
        updatedItem.vatAmount = Math.round(updatedItem.netAmount * (vatPercent / 100));
        updatedItem.grossAmount = updatedItem.netAmount + updatedItem.vatAmount;
      }

      newItems[index] = updatedItem;
      return { ...prev, items: newItems };
    });
  };

  const addItem = () => {
    const newItem: FormInvoiceItem = {
      id: `item-new-${Date.now()}`,
      description: '',
      quantity: 1,
      unit: 'db',
      unitPrice: 0,
      vatRate: '27',
      netAmount: 0,
      vatAmount: 0,
      grossAmount: 0,
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const netTotal = formData.items.reduce((sum, item) => sum + item.netAmount, 0);
    const vatTotal = formData.items.reduce((sum, item) => sum + item.vatAmount, 0);
    const grossTotal = netTotal + vatTotal;
    // FIN-FG-4-6 + FK-4: Deduct advance from final total (quote-linked OR manual).
    // bug-screen #5: a manuális %-előleg a LIVE grossTotal-ból derive-álódik
    // (követi a tétel-változást), nem egy befagyott összegből.
    const advanceDeduction =
      advanceInfo?.advanceAmount ??
      deriveManualAdvance(grossTotal, manualAdvancePercent, manualAdvanceAmount);
    const finalTotal = Math.max(0, grossTotal - advanceDeduction);
    return { netTotal, vatTotal, grossTotal, advanceDeduction, finalTotal };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.partnerName.trim()) {
      alert(t('invoice.partnerNameValidation'));
      return;
    }

    if (formData.items.length === 0) {
      alert(t('invoice.itemsValidation'));
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode && id) {
        // Update existing invoice (only mutable fields)
        await api.patch(`/invoices/${id}`, {
          dueDate: formData.dueDate,
          notes: formData.notes || undefined,
          paymentMethod: formData.paymentMethod.toUpperCase(),
        });

        const totals = calculateTotals();
        alert(
          t('invoice.invoiceCreated', {
            action: t('invoice.updatedAction'),
            number: id,
            amount: formatPrice(totals.grossTotal),
          })
        );
        navigate(`/invoices/${id}`);
      } else {
        // Create new invoice via real API — backend generates invoice number
        const response = await createInvoice({
          partnerId: formData.partnerId || formData.partnerName, // fallback to name if no ID
          partnerName: formData.partnerName,
          partnerAddress: formData.partnerAddress,
          partnerTaxNumber: formData.partnerTaxNumber || undefined,
          type: formData.type.toUpperCase() as
            | 'STANDARD'
            | 'ADVANCE'
            | 'FINAL'
            | 'PROFORMA'
            | 'CREDIT_NOTE'
            | 'CORRECTION',
          invoiceDate: formData.issueDate,
          dueDate: formData.dueDate,
          paymentMethod: formData.paymentMethod.toUpperCase() as
            | 'CASH'
            | 'CARD'
            | 'TRANSFER'
            | 'COD',
          notes: formData.notes || undefined,
          items: formData.items.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitPriceNet: item.unitPrice,
            vatRate: mapVatRateToApi(item.vatRate),
          })),
        });

        const createdInvoice = response.data;
        alert(
          t('invoice.invoiceCreated', {
            action: t('invoice.createdAction'),
            number: createdInvoice.invoiceNumber,
            amount: formatPrice(Number(createdInvoice.grossAmount)),
          })
        );
        navigate(`/invoices/${createdInvoice.id}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      alert(`${t('invoice.saveError')}: ${message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const totals = calculateTotals();
  // bug-screen #5: a manuális-előleg megjelenítése (amount-input + levonás-sor) a
  // LIVE értéket tükrözi (%-mód → derive a grossTotal-ból; fix-mód → a beírt összeg).
  const manualAdvance = deriveManualAdvance(
    totals.grossTotal,
    manualAdvancePercent,
    manualAdvanceAmount
  );

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/invoices')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {isEditMode ? t('invoice.editInvoice') : t('invoice.newInvoice')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isEditMode ? t('invoice.editInvoiceSubtitle') : t('invoice.newInvoiceSubtitle')}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-primary hover:bg-primary/90"
          >
            {isSaving ? t('invoice.savingInProgress') : t('common.save')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Invoice Type & Status */}
          <Card>
            <CardHeader>
              <CardTitle>{t('invoice.invoiceType')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common.type')}
                  </label>
                  <select
                    value={formData.type}
                    onChange={e => updateField('type', e.target.value as InvoiceType)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {INVOICE_TYPES.map(tp => (
                      <option key={tp.value} value={tp.value}>
                        {t(tp.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('table.status')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={e => updateField('status', e.target.value as InvoiceStatus)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {INVOICE_STATUSES.map(s => (
                      <option key={s.value} value={s.value}>
                        {t(s.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('invoice.paymentMethod')}
                  </label>
                  <select
                    value={formData.paymentMethod}
                    onChange={e => updateField('paymentMethod', e.target.value as PaymentMethod)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {PAYMENT_METHODS.map(p => (
                      <option key={p.value} value={p.value}>
                        {t(p.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Partner Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('invoice.partnerData')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <PartnerSearchSelect
                variant="list"
                value={selectedPartner}
                onSelect={partner => {
                  setSelectedPartner(partner);
                  setFormData(prev => ({
                    ...prev,
                    partnerId: partner.id,
                    partnerName: partner.name,
                    partnerAddress: partner.address ?? '',
                    partnerTaxNumber: partner.taxNumber ?? '',
                  }));
                }}
              />
              {selectedPartner && (
                <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
                  <p className="text-sm font-medium text-foreground">{selectedPartner.name}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('invoice.addressLabel')}
                      </label>
                      <Input
                        value={formData.partnerAddress}
                        onChange={e => updateField('partnerAddress', e.target.value)}
                        placeholder={t('invoice.addressPlaceholder')}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        {t('invoice.taxNumber')}
                      </label>
                      <Input
                        value={formData.partnerTaxNumber}
                        onChange={e => updateField('partnerTaxNumber', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle>{t('invoice.dates')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('invoice.issueDateLabel')}
                  </label>
                  <Input
                    type="date"
                    value={formData.issueDate}
                    onChange={e => updateField('issueDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('invoice.dueDateLabel')}
                  </label>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={e => updateField('dueDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('invoice.totalGross')}
                  </label>
                  <p className="mt-2 text-xl font-bold text-primary">
                    {formatPrice(totals.grossTotal)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* FIN-FG-4-6: Advance Payment Info Banner */}
          {advanceLoading && (
            <Card>
              <CardContent className="py-4">
                <div className="h-4 w-48 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          )}
          {advanceInfo && (
            <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-blue-700 dark:text-blue-300">
                  {t('invoice.advance.deductionLine')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('invoice.advance.advanceAmount')}
                    </p>
                    <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                      {formatPrice(advanceInfo.advanceAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{advanceInfo.quoteNumber}</p>
                    <p className="text-sm font-medium text-foreground">{advanceInfo.partnerName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('quote.advance.paidAt')}</p>
                    <p className="text-sm text-foreground">
                      {new Date(advanceInfo.advancePaidAt).toLocaleDateString('hu-HU')}
                    </p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('invoice.advance.remainingAmount')}: {formatPrice(totals.finalTotal)}
                </p>
              </CardContent>
            </Card>
          )}

          {/* FK-4: Manual advance input (when no quote-linked advance) */}
          {!advanceInfo && !isEditMode && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('invoice.advance.manualTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      {t('invoice.advance.percentLabel')}
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={manualAdvancePercent ?? ''}
                      onChange={e => {
                        // bug-screen #5: NEM fagyasztjuk be az összeget — a %-ot
                        // tároljuk, a deriveManualAdvance a LIVE grossTotal-ból számol.
                        const pct = e.target.value ? Number(e.target.value) : null;
                        setManualAdvancePercent(pct);
                        setManualAdvanceAmount(null);
                      }}
                      placeholder={t('invoice.advance.percentPlaceholder')}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      {t('invoice.advance.amountLabel')}
                    </label>
                    <Input
                      type="number"
                      min={0}
                      value={
                        manualAdvancePercent != null
                          ? manualAdvance || ''
                          : (manualAdvanceAmount ?? '')
                      }
                      onChange={e => {
                        const amt = e.target.value ? Number(e.target.value) : null;
                        setManualAdvanceAmount(amt);
                        setManualAdvancePercent(null);
                      }}
                      placeholder={t('invoice.advance.amountPlaceholder')}
                      className="text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    {manualAdvance > 0 && (
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        {t('invoice.advance.deductionLine')}: {formatPrice(manualAdvance)}
                      </p>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('invoice.advance.manualHint')}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Items */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('invoice.items')}</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  {t('invoice.addItem')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {formData.items.map((item, index) => (
                  <div key={item.id} className="p-4">
                    <div className="grid gap-4 sm:grid-cols-12">
                      <div className="sm:col-span-3">
                        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                          {t('invoice.description')}
                        </label>
                        <Input
                          value={item.description}
                          onChange={e => updateItem(index, 'description', e.target.value)}
                          placeholder={t('invoice.itemDescriptionPlaceholder')}
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                          {t('invoice.quantityShort')}
                        </label>
                        <Input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={e => updateItem(index, 'quantity', Number(e.target.value))}
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                          {t('invoice.unit')}
                        </label>
                        <Input
                          value={item.unit}
                          onChange={e => updateItem(index, 'unit', e.target.value)}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                          {t('invoice.unitPriceNet')}
                        </label>
                        <Input
                          type="number"
                          min={0}
                          value={item.unitPrice}
                          onChange={e => updateItem(index, 'unitPrice', Number(e.target.value))}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                          {t('invoice.vatPercent')}
                        </label>
                        <select
                          value={item.vatRate}
                          onChange={e => updateItem(index, 'vatRate', e.target.value)}
                          className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        >
                          {VAT_RATES.map(vr => (
                            <option key={vr.value} value={vr.value}>
                              {t(vr.labelKey)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                          {t('invoice.grossAmount')}
                        </label>
                        <p className="mt-2 font-medium text-gray-900 dark:text-white">
                          {formatPrice(item.grossAmount)}
                        </p>
                      </div>
                      <div className="flex items-end sm:col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          {t('invoice.deleteItem')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {formData.items.length === 0 && (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {t('invoice.noItemsClickAdd', { button: t('table.addItem') })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Totals Summary */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('invoice.netTotal')}</span>
                    <span className="text-foreground">{formatPrice(totals.netTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('invoice.vatLabel')}</span>
                    <span className="text-foreground">{formatPrice(totals.vatTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-1">
                    <span className="text-foreground font-medium">{t('invoice.grossTotal')}</span>
                    <span className="text-foreground font-medium">
                      {formatPrice(totals.grossTotal)}
                    </span>
                  </div>
                  {/* FIN-FG-4-6 + FK-4: Advance deduction line (quote-linked OR manual) */}
                  {totals.advanceDeduction > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
                        <span>{t('invoice.advance.deductionLine')}</span>
                        <span>-{formatPrice(totals.advanceDeduction)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 font-bold">
                        <span className="text-foreground">
                          {t('invoice.advance.remainingAmount')}
                        </span>
                        <span className="text-primary">{formatPrice(totals.finalTotal)}</span>
                      </div>
                    </>
                  )}
                  {totals.advanceDeduction <= 0 && (
                    <div className="flex justify-between border-t pt-2 font-bold">
                      <span className="text-foreground">{t('common.total')}</span>
                      <span className="text-primary">{formatPrice(totals.grossTotal)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('common.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                rows={4}
                className="w-full rounded-md border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                placeholder={t('invoice.notesPlaceholder')}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/invoices')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSaving} className="bg-primary hover:bg-primary/90">
              {isSaving
                ? t('invoice.savingInProgress')
                : isEditMode
                  ? t('invoice.saveChangesButton')
                  : t('invoice.createInvoiceButton')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
